import _ from 'underscore';
import React, {
	useEffect,
	useRef,
	useState,
	useCallback,
	useMemo
} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { api } from '@culture/common-helpers/request';

import escapeStringRegexp from 'escape-string-regexp';
import Popup from 'components/Popup';
import { DEFAULT_LIMIT } from 'utils/helpers/constants';
import FilterButton from '../FilterButton';

import List from './components/List';

import styles from './styles.module.scss';

const CustomSelect = ({
	name,
	customChildren,
	searchable,
	placeholder,
	resetOption,
	selectedValue,
	controlTitleField,
	accent,
	onChange,
	isLoading,
	options,
	valueField,
	maxTitleLength,
	style,
	apiParams
}) => {
	const [selected, setSelected] = useState(null);
	const [visibility, setVisibility] = useState(false);

	const [searchValue, setSearchValue] = useState('');
	const [previousSearchValue, setPreviousSearchValue] = useState('');

	const [isUpdating, setIsUpdating] = useState(isLoading);
	const [isLoadingMore, setIsLoadingMore] = useState(false);

	const [optionsList, setOptionsList] = useState(options || []);

	const controlRef = useRef(null);

	const handleSearch = (event) => {
		setPreviousSearchValue(searchValue);
		setSearchValue(event.target.value);
	};

	const selectedOption =
		_(options).findWhere({ [valueField]: selectedValue }) || resetOption;

	const initialTitleField = selectedOption[controlTitleField];
	const isTitleLongerThanMaxValue =
		maxTitleLength && initialTitleField.length > maxTitleLength * 1.2;

	const checkOnMaxValue = (positive, negative) =>
		isTitleLongerThanMaxValue ? positive : negative;

	const filterButtonTitle = checkOnMaxValue(
		`${initialTitleField?.slice(0, maxTitleLength)}...`,
		initialTitleField
	);
	const fetchData = useMemo(() => {
		if (_.isEmpty(apiParams)) {
			return null;
		}

		return ({ query, cached = false, extraParams, isLoadMore = false }) => {
			const { url, affectQueryParams } = apiParams;
			if (isLoadMore) {
				setIsLoadingMore(true);
			} else {
				setIsUpdating(true);
			}
			return api(url, {
				...affectQueryParams,
				...extraParams,
				cached,
				query
			})
				.then((data) => {
					const items = data?.locales;

					if (items.length === 0 && isLoadMore) {
						return;
					}

					setOptionsList((prevState) =>
						_.uniq(
							isLoadMore ? [...prevState, ...items] : items,
							(item) => item._id
						)
					);
				})
				.finally(() => {
					_.delay(() => {
						setIsUpdating(false);
						setIsLoadingMore(false);
					}, 300);
				});
		};
	}, [apiParams?.affectQueryParams]);

	const fetchDataDebounced = useMemo(
		() => fetchData && _.debounce(fetchData, apiParams.debounce),
		[fetchData]
	);

	useEffect(() => {
		if (fetchData && !_(apiParams).isEmpty()) {
			fetchDataDebounced.cancel();
			fetchData({ query: '', cached: true });
		} else {
			const searchRegexp = new RegExp(escapeStringRegexp(searchValue), 'i');
			const preparedOptions = options.filter(({ title }) =>
				searchRegexp.test(title)
			);
			setOptionsList(preparedOptions);
		}
	}, [options]);

	useEffect(() => {
		if (searchable && searchValue) {
			fetchDataDebounced({ query: searchValue });
		}
		if (!searchValue && previousSearchValue) {
			fetchDataDebounced({ query: '' });
		}
	}, [searchValue]);

	const onSelect = (value) => {
		setVisibility(false);
		setSelected(value);
	};

	const toggleList = () => {
		setSearchValue('');
		setVisibility((prevVisibility) => !prevVisibility);
	};

	const onAfterClose = () => {
		if (selected || (resetOption && selected === resetOption[valueField])) {
			onChange(name, selected);
			setSelected(null);
		}

		setSearchValue('');
		setPreviousSearchValue('');
	};

	const buttonCx = cx(styles.button, styles.CustomSelect__Button, {
		[styles.active]: accent
	});

	const customInnerChildren =
		typeof customChildren === 'function'
			? customChildren(() => setVisibility(false))
			: customChildren;

	const loadMore = useCallback(
		async () =>
			!!apiParams &&
			fetchData({
				query: searchValue,
				cached: true,
				extraParams: {
					...apiParams?.affectQueryParams,
					limit: DEFAULT_LIMIT,
					offset: optionsList.length
				},
				isLoadMore: true
			}),
		[optionsList]
	);

	return (
		<div>
			<div className={styles.CustomSelect} ref={controlRef}>
				<FilterButton
					className={buttonCx}
					onClick={toggleList}
					reverse
					title={<span>{filterButtonTitle}</span>}
					iconClassName={cx(styles.CustomSelect__Icon, {
						[styles.CustomSelect__Icon_reverse]: visibility
					})}
					icon="chevronDown"
					style={style}
				/>
			</div>
			<Popup
				isOpen={visibility}
				element={controlRef.current}
				onClickOutside={toggleList}
				onExited={onAfterClose}
				placement="bottom-start"
				theme="widget"
				options={{
					strategy: 'absolute',
					modifiers: [
						{
							name: 'flip',
							options: {
								fallbackPlacements: ['bottom']
							}
						}
					]
				}}
			>
				{customInnerChildren || (
					<List
						{...{
							searchable,
							placeholder,
							selectedValue,
							resetOption,
							searchValue,
							valueField,
							loadMore,
							isLoadingMore
						}}
						onSearch={handleSearch}
						isLoading={isUpdating}
						options={optionsList}
						onChange={onSelect}
					/>
				)}
			</Popup>
		</div>
	);
};

CustomSelect.propTypes = {
	name: PropTypes.string.isRequired,
	customChildren: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
	options: PropTypes.arrayOf(
		PropTypes.shape({
			title: PropTypes.string.isRequired,
			name: PropTypes.string
		})
	),
	apiParams: PropTypes.shape({
		url: PropTypes.string,
		debounce: PropTypes.number,
		affectQueryParams: PropTypes.shape({})
	}),
	isLoading: PropTypes.bool,
	selectedValue: PropTypes.node,
	onChange: PropTypes.func,
	searchable: PropTypes.bool,
	placeholder: PropTypes.string,
	resetOption: PropTypes.shape({
		sysName: PropTypes.string,
		title: PropTypes.string.isRequired,
		name: PropTypes.string
	}),
	valueField: PropTypes.string,
	controlTitleField: PropTypes.string,
	label: PropTypes.string,
	accent: PropTypes.bool,
	style: PropTypes.object,
	maxTitleLength: PropTypes.number,
	hasClearButton: PropTypes.bool
};

CustomSelect.defaultProps = {
	options: [],
	isLoading: false,
	onChange: _.noop,
	selectedValue: '',
	label: '',
	searchable: false,
	valueField: 'name',
	resetOption: { sysName: 'russia', title: 'По всей России' },
	controlTitleField: 'title',
	accent: false,
	hasClearButton: false
};

export default CustomSelect;
